import React, { useEffect, useRef, useState } from 'react';
import '@arcgis/core/assets/esri/themes/light/main.css';
import Map from '@arcgis/core/Map';
import MapView from '@arcgis/core/views/MapView';
import TileLayer from '@arcgis/core/layers/TileLayer';
import Basemap from '@arcgis/core/Basemap';

import Compass from '@arcgis/core/widgets/Compass';
import Locate from '@arcgis/core/widgets/Locate';
import Zoom from '@arcgis/core/widgets/Zoom';
import Search from '@arcgis/core/widgets/Search';
import BasemapToggle from '@arcgis/core/widgets/BasemapToggle';
import Graphic from '@arcgis/core/Graphic';
import PictureMarkerSymbol from "@arcgis/core/symbols/PictureMarkerSymbol";


import '../../assets/styles/custom.css';
import { appConfig } from "../../appConfig";


const ArcGISMap = ({ onShowCoveragePopup }) => {
    const { ArcGISMap: cfg } = appConfig;

    const [show2G, setShow2G] = useState(true);
    const [show4G, setShow4G] = useState(true);
    const [show5G, setShow5G] = useState(true);
    const [inputValue, setInputValue] = useState('');
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [isMapLoading, setIsMapLoading] = useState(true);

    const mapRef = useRef(null);
    const widgetDiv = useRef(null);
    const searchRef = useRef(null);
    const mapViewRef = useRef(null);
    const initializedRef = useRef(false);

    useEffect(() => {
        if (initializedRef.current) return;

        initializedRef.current = true;

        widgetDiv.current.innerHTML = '';

        // =======================
        // BASEMAPS WITH THUMBNAIL
        // =======================

        // Hybrid (Default)
        // Hybrid
        const basemapHybrid = new Basemap({
            portalItem: {
                id: "c14ef2a973af4998a6d8cf268ff64806"
            }
        });

        basemapHybrid.load().then(() => {
            basemapHybrid.thumbnailUrl = cfg.basemaps.hybrid.thumbnail;
        });


        // Topo
        const basemapTopo = new Basemap({
            portalItem: {
                id: "5ea9dbd0094943b3ac41c569d2868985"
            }
        });

        basemapTopo.load().then(() => {
            basemapTopo.thumbnailUrl = cfg.basemaps.topo.thumbnail;
        });

        // Create map
        const map = new Map({
            basemap: basemapTopo
        });


        const view = new MapView({
            container: mapRef.current,
            map,
            center: [78.2, 25.2],
            zoom: 4.6,
            qualityProfile: "high",
            constraints: { minZoom: 3, maxZoom: 15 },
            ui: { components: [] }
        });

        mapViewRef.current = view;


        // SEARCH
        const search = new Search({
            view,
            locationEnabled: false,
            popupEnabled: false,
            suggestSearch: true,
            minSuggestCharacters: 2,
            maxSuggestions: 6,
            includeDefaultSources: true,
            resultGraphicEnabled: false   // 🔥 IMPORTANT
        });

        searchRef.current = search;
        search.on("search-complete", (e) => {
            const r = e.results?.[0]?.results?.[0];
            if (!r) return;

            const pt = r.feature.geometry;

            mapViewRef.current.goTo({ target: pt, zoom: 15 });

            mapViewRef.current.graphics.removeAll();
            mapViewRef.current.graphics.add(
                new Graphic({
                    geometry: pt,
                    symbol: new PictureMarkerSymbol({
                        url: "https://assets.airtel.in/static-assets/wirelesscoverage/locate.svg",
                        width: "28px",
                        height: "28px",
                        yoffset: "14px"
                    })
                })
            );
        });


        // COVERAGE LAYERS
        const layers = [];

        Object.values(cfg.coverageLayers).forEach((l) => {
            const layer = new TileLayer({
                url: l.url,
                id: l.id,
                visible: true,
                opacity: 0.9
            });

            layers.push(layer);
            map.add(layer);
        });
        view.when(async () => {

            await Promise.all(layers.map(layer => layer.when()));
            setIsMapLoading(false);
            view.map.basemap.baseLayers.forEach(layer => {
                layer.opacity = 0.82;
            });
            // ✅ FORCE RENDER (IMPORTANT FIX)
            setTimeout(() => {
                view.goTo({
                    center: view.center,
                    zoom: view.zoom
                });
            }, 300);
            if (!widgetDiv.current) return;
            widgetDiv.current.innerHTML = '';

            // LOCATE + ZOOM (custom container)
            const locate = new Locate({ view, container: document.createElement('div') });
            const zoom = new Zoom({ view, layout: 'vertical', container: document.createElement('div') });

            widgetDiv.current.appendChild(locate.container);
            widgetDiv.current.appendChild(zoom.container);

            // COMPASS (ArcGIS UI)
            const compass = new Compass({ view });
            view.ui.add(compass, 'top-right');

            // BASEMAP TOGGLE (ArcGIS UI)
            const basemapToggle = new BasemapToggle({
                view,
                nextBasemap: basemapHybrid
            });
            view.ui.add(basemapToggle, 'bottom-right');
        });

        return () => {
            if (searchRef.current) {
                searchRef.current.destroy();
            }
            if (mapViewRef.current) {
                mapViewRef.current.destroy();
            }
            initializedRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleInputChange = async (e) => {
        const value = e.target.value;
        setInputValue(value);

        if (!searchRef.current || value.length < 2) {
            setSuggestions([]);
            return;
        }

        try {
            const res = await searchRef.current.suggest(value);

            // ✅ CORRECT PATH
            const list = res?.results?.[0]?.results || [];

            setSuggestions(list);
        } catch (err) {
            console.error("Suggest error:", err);
            setSuggestions([]);
        }
    };


    const handleKeyDown = async (e) => {
        if (e.key === 'Enter' && inputValue.trim()) {
            setSuggestions([]);
            searchRef.current?.search(inputValue);
        }
    };
    const handleSuggestionClick = (suggestion) => {
        setInputValue(suggestion.text);
        setSuggestions([]);
        searchRef.current?.search(suggestion.text);
    };

    const toggleLayer = (id) => {
        const layer = mapViewRef.current?.map.findLayerById(id);
        if (!layer) return;
        layer.visible = !layer.visible;

        if (id === 'layer1') setShow2G(layer.visible);
        if (id === 'layer2') setShow4G(layer.visible);
        if (id === 'layer3') setShow5G(layer.visible);
    };

    const mapHeight = isFullscreen
        ? window.innerHeight
        : window.innerHeight - 133;

    const toggleFullscreen = () => {
        setIsFullscreen(prev => !prev);

        setTimeout(() => {
            window.dispatchEvent(new Event("resize"));
        }, 300);
    };
    return (

        <div
            className={`map-container ${isFullscreen ? 'fullscreen' : ''}`}
            style={{ height: mapHeight }}
        >
            {isMapLoading && (
                <div className="map-loader">
                    <div className="spinner"></div>
                </div>
            )}

            <div
                ref={mapRef}
                style={{
                    height: '100%',
                    width: '100%',
                    minHeight: isFullscreen ? '100vh' : 'auto'
                }}
            />

            <div className="right-tools">
                {/* Fullscreen */}
                <button
                    className="fullscreen-btn"
                    title={isFullscreen ? "Exit Full Screen" : "Full Screen"}
                    onClick={toggleFullscreen}
                >
                    <span
                        className={`esri-icon ${isFullscreen ? "esri-icon-zoom-out-fixed" : "esri-icon-zoom-in-fixed"
                            }`}
                    />
                </button>

                {/* Coverage info */}
                <button
                    className="coverage-info-btn"
                    title="Coverage last updated"
                    onClick={onShowCoveragePopup}
                >

                    <span className="esri-icon-notice-round" />

                </button>
            </div>



            {/* Locate + Zoom */}
            <div
                ref={widgetDiv}
                style={{
                    position: 'absolute',
                    bottom: '24px',
                    right: '15px',
                    zIndex: 99,
                    background: 'white',
                    borderRadius: '12px'
                }}
            />

            {/* Search */}
            {/* Search */}
            <div className="inputWrapperStyle">
                <input
                    className="inputStyle"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    placeholder={cfg.search.placeholder}
                />

                {/* Clear (X) button */}
                {inputValue && (
                    <button
                        className="clear-btn"
                        title="Clear"
                        onClick={() => {
                            setInputValue('');
                            setSuggestions([]);
                            mapViewRef.current?.graphics.removeAll();
                        }}
                    >
                        <span className="esri-icon-close" />
                    </button>
                )}

                {/* Search icon */}
                <button
                    className="search-btn"
                    onClick={() => searchRef.current?.search(inputValue)}
                >
                    <span className="esri-icon-search" />
                </button>

                {suggestions.length > 0 && (
                    <ul className="suggestion-list">
                        {suggestions.map((s, i) => (
                            <li key={i} onClick={() => handleSuggestionClick(s)}>
                                {s.text}
                            </li>
                        ))}
                    </ul>
                )}

            </div>


            {/* Coverage buttons */}
            <div className="coveragebutton d-flex gap-2">
                <button className={`btn btn-sm rounded-pill twoG ${show2G ? 'selected' : ''}`} onClick={() => toggleLayer('layer1')}>
                    <i className="fa fa-circle me-1" style={{ color: '#5b5bb0' }} /> 2G Coverage
                </button>

                <button className={`btn btn-sm rounded-pill fourG ${show4G ? 'selected' : ''}`} onClick={() => toggleLayer('layer2')}>
                    <i className="fa fa-circle me-1" style={{ color: '#800080' }} /> 4G Coverage
                </button>

                <button className={`btn btn-sm rounded-pill fiveG ${show5G ? 'selected' : ''}`} onClick={() => toggleLayer('layer3')}>
                    <i className="fa fa-circle me-1" style={{ color: '#dddd41' }} /> 5G Coverage
                </button>
            </div>
        </div>
    );
};

export default ArcGISMap;
